import { type ReactNode } from "react";
import { ChevronLeft, type LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils/cn";
import { usePillNavHidden } from "./BottomChrome";

export interface PageBottomActionBarBack {
  /**
   * Where the 44pt circular back button goes. W35.6 made this the standard
   * left-hand companion of the primary CTA on every create / edit page, so
   * it gets a first-class prop rather than being spelled as a `secondary`
   * entry by each call site.
   */
  onClick: () => void;
  /** Accessible label (aria-label) — required, the button is icon-only. */
  label: string;
  /** Optional override; defaults to `ChevronLeft`. */
  icon?: LucideIcon;
  disabled?: boolean;
  testId?: string;
}

export interface PageBottomActionBarSecondary {
  /** lucide-react icon component (NOT emoji — per DESIGN.md §11 ban). */
  icon: LucideIcon;
  /** Accessible label (aria-label) — required because the button is icon-only. */
  label: string;
  onClick: () => void;
  disabled?: boolean;
  testId?: string;
}

export interface PageBottomActionBarPrimary {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  testId?: string;
}

export interface PageBottomActionBarProps {
  /**
   * Visibility toggle. When `false`, slides down off-screen (`translate-y-full`).
   * When `true`, slides up (`translate-y-0`). CSS-only — NO inline style.transform
   * (per `component-api.md §10` ban + AGENTS.md §3 #15 Apple HIG rule).
   *
   * Default: `true`.
   */
  visible?: boolean;
  /** Primary CTA. Required. */
  primary: PageBottomActionBarPrimary;
  /**
   * 44pt circular back button, rendered left of the primary CTA.
   *
   * W35.6: every create / edit page pairs its submit action with a back
   * affordance, so leaving the page is always one tap from the action you
   * came to perform.
   */
  back?: PageBottomActionBarBack;
  /** Up to 2 secondary icon-only buttons rendered on the LEFT of the primary CTA. */
  secondary?: readonly PageBottomActionBarSecondary[];
  /** Wrapper testId for the root container (handy for E2E + screenshot automation). */
  testId?: string;
}

/**
 * PageBottomActionBar — Apple HIG docked bottom action area.
 *
 * Per `docs/spec/w2-frontend-components.md §3.2`:
 *   - Replaces the banned Material FAB (`fixed bottom-N right-N` round button).
 *   - 1 primary CTA (pill button) + 0-2 secondary icon buttons on the left.
 *   - Slide-up animation via `data-visible` attribute (no inline transform).
 *
 * **W51 (D-6) — the bar is now a floating pill in the nav's material, not a
 * full-bleed glass strip.** The user's wording was 「把所有的底部 action Bar 更換成
 * 與 pill nav bar 一致的懸浮 pill 樣式」, and the previous shape (a warm-white
 * `glass-1` band spanning `inset-x-0`) was the *opposite* of the navy L3 nav
 * sitting directly above it — `DESIGN.md §1.5` makes navy 「全站最深最飽和的東西」.
 *
 * Geometry, colour and the CTA's contrast pairing now live in `.action-pill`
 * (`globals.css`), written to match `.nav-pill` property for property, and
 * guarded by `__tests__/action-bar-navy-material.test.ts`. They are not inline
 * here because a comparison between the two classes is the guard — inline
 * values made it impossible, and the values drifted 2px apart the moment they
 * were written separately.
 *
 * Used by: DiaryEditor (Save), PairInvitePage (Copy + Share), PairChoicePage
 * (Join), AnniversaryCreate, WishlistCreate, ForgotPassword (Send),
 * ResetPassword (Confirm).
 *
 * Hidden when: keyboard open (consumers should toggle `visible={!keyboardOpen}`)
 * + auth/landing routes (don't render the component at all there).
 */
export function PageBottomActionBar({
  visible = true,
  primary,
  back,
  secondary,
  testId,
}: PageBottomActionBarProps) {
  const pillNavHidden = usePillNavHidden();
  const secondaryCount = secondary?.length ?? 0;
  // Hard cap at 2 per spec — extra are silently dropped (and warned in dev).
  const visibleSecondary =
    secondaryCount > 2
      ? (() => {
          if (import.meta.env.DEV) {
            console.warn(
              `[PageBottomActionBar] received ${secondaryCount} secondary buttons; cap is 2 — extras dropped.`,
            );
          }
          return secondary!.slice(0, 2);
        })()
      : (secondary ?? []);

  return (
    <div
      data-component="page-bottom-action-bar"
      data-visible={visible ? "true" : "false"}
      data-secondary-count={visibleSecondary.length}
      // W52.4 — whether the PillNav is still on screen, so `.action-pill` can
      // decide the slot. This used to be two Tailwind arbitrary `bottom-*` classes
      // chosen in JSX; it cannot stay there, because `.action-pill` now declares
      // `bottom` and **unlayered CSS beats `@layer utilities` whatever the
      // specificity** — the class would have kept rendering while doing nothing.
      data-nav-visible={pillNavHidden ? "false" : "true"}
      data-testid={testId ?? "page-bottom-action-bar"}
      aria-hidden={!visible}
      className={cn(
        // W51 (D-6) — **geometry** moves, not just material. The user's words:
        // 「把所有的底部 action Bar 更換成與 pill nav bar 一致的懸浮 pill 樣式」.
        //
        // `.action-pill` is a class rather than a long class list so that
        // `G10` can compare it to `.nav-pill` **property by property**. Writing
        // the values inline would make the comparison impossible, and the
        // comparison is the whole guard — the first draft of this file was
        // written inline and drifted 2px taller (`padding: 8` → 62px against a
        // 60px nav), which is exactly the kind of difference two adjacent
        // floating objects cannot have.
        "action-pill fixed z-20",
        // Slide-up animation — CSS-only. The `translateX(-50%)` centring lives in
        // `.action-pill` alongside the slide, because Tailwind's
        // `translate-y-*` utilities compose their own `transform` and would
        // **overwrite** the centring rather than add to it. Two systems writing
        // `transform` is how a pill ends up 149px off-centre.
        "transition-transform duration-base ease-quiet",
        // Disable interaction while hidden so a11y tools don't catch it
        // AND so a click on an invisible bar can't sneak through.
        "data-[visible=false]:pointer-events-none",
        // W52.4 — **no `bottom-*` class here any more.** The slot belongs to
        // `.action-pill`, which reads `data-nav-visible` above:
        //
        //   page owns the edge   → `bottom: var(--pill-bottom)`  = the nav's slot
        //   nav still on screen  → `bottom: calc(--pill-bottom + --pill-h + gap)`
        //   ≥ 768px              → `bottom: 0`                   = the old `md:bottom-0`
        //
        // It was `env(safe-area-inset-bottom)` when claimed, which measures **0px**
        // in a mobile browser: the bar sat flush against the screen edge while the
        // nav it is a copy of floated 10px above it. That value came from W51
        // (`ADR-0164`), and its reason was sound — a floating pill lifted clear of
        // the home indicator is better than a full-bleed band exposing the page
        // ground behind it — but it answered 「clear the gesture zone」 when the
        // question was 「be the nav」, and the nav's own `0.6` factor is the
        // recorded answer to the gesture-zone question. Reversing W51's branch and
        // why is ADR-0174; the measurement is in `globals.css` at `.action-pill`.
        //
        // And it could not have stayed in JSX anyway — see `data-nav-visible` above
        // for why a Tailwind utility loses to this class now.
      )}
    >
      {/* `w-full` is load-bearing and was missing on the first pass.

          `.action-pill` is `display: flex; justify-content: center`, so this row
          is sized by its **content** and centred — measured 144px of contents
          inside a 298px pill, with ~77px of empty navy on each side and the CTA
          squeezed to exactly its label with zero horizontal padding. The
          screenshot showed the text running to the lilac's edge.

          The nav's own contents fill it because its slots are `w-44 flex-none`
          and there are four of them. Nothing about a flex item grows by
          default; the row has to be told to.
      */}
      <div className="flex w-full items-center gap-2 px-1.5">
        {back ? <BackButton back={back} /> : null}
        {visibleSecondary.length > 0 ? (
          <div className="flex items-center gap-1.5">
            {visibleSecondary.map((s, i) => {
              const Icon = s.icon;
              return (
                <button
                  key={`${s.label}-${i}`}
                  type="button"
                  onClick={s.onClick}
                  disabled={s.disabled}
                  aria-label={s.label}
                  title={s.label}
                  data-testid={s.testId ?? `page-bottom-action-bar-secondary-${i}`}
                  className={cn(
                    "tap-target inline-flex h-11 w-11 items-center justify-center rounded-full",
                    // W51 §2.6b — the nav's own secondary idiom: white/10 on
                    // navy. `bg-paper-deep` would be near-white on near-black.
                    "bg-white/10 text-white hover:bg-white/15 border border-white/15",
                    "press transition-colors duration-quick ease-paper",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                  )}
                >
                  <Icon size={20} aria-hidden />
                </button>
              );
            })}
          </div>
        ) : null}
        {/* W51 §2.6b — the CTA is **lilac + ink-1**, and that is arithmetic.
            `you-strong` rgb(60 110 165) on chrome-navy rgb(31 37 54) measures
            **2.89:1**, below the 3:1 floor for non-text UI boundaries; white on
            lilac is 2.69:1, which fails AA. ink-1 on lilac is **6.32:1**, and it
            is the pair `.nav-item-on` already uses. Changing this colour by eye
            puts one of those two failures back.

            **`bg-surface-lilac`, not `bg-lilac`.** The first draft wrote
            `bg-lilac`, which is the *mockup's* vocabulary; the build's theme
            names the token `surface-lilac` (`tailwind.config.ts`), so `bg-lilac`
            emitted **no CSS at all** — and the CTA rendered as ink-1 text on a
            transparent background over navy, which is to say invisible. Nothing
            caught it: `tsc` accepts any string, every unit test that only reads
            the class list still passed, and the computed style reported
            `rgba(0, 0, 0, 0)` rather than an error. Only a screenshot of the
            running app showed it, which is the same trap as W47's
            `rounded-inner`. */}
        <button
          type="button"
          onClick={primary.onClick}
          disabled={primary.disabled || primary.loading}
          data-testid={primary.testId ?? "page-bottom-action-bar-primary"}
          className={cn(
            "tap-target press inline-flex h-11 flex-1 items-center justify-center gap-s-2",
            // Horizontal padding so the label never sits flush against the
            // lilac's edge. Measured 0/0 on the first pass, which is what made a
            // long label like 「寫這篇日記」 read as clipped rather than tight.
            "rounded-pill bg-surface-lilac text-ink-1 font-semibold px-4",
            "transition-transform duration-quick ease-quiet",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            primary.loading && "opacity-80 cursor-progress",
          )}
          aria-busy={primary.loading || undefined}
        >
          {primary.leadingIcon}
          {primary.loading ? "處理中…" : primary.label}
          {primary.trailingIcon}
        </button>
      </div>
    </div>
  );
}

/**
 * W35.6 — the 44pt circular companion to the primary CTA.
 *
 * Shares the secondary button's chrome deliberately: same `h-11 w-11`
 * (44px per Apple HIG §15), same `rounded-full`, same hairline + press
 * feedback, so the row reads as one control group rather than a back button
 * that looks bolted on. `ChevronLeft` is the default glyph.
 */
function BackButton({ back }: { back: PageBottomActionBarBack }) {
  const Icon = back.icon ?? ChevronLeft;
  return (
    <button
      type="button"
      onClick={back.onClick}
      disabled={back.disabled}
      aria-label={back.label}
      title={back.label}
      data-testid={back.testId ?? "page-bottom-action-bar-back"}
      className={cn(
        "tap-target inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full",
        // W51 — same navy idiom as the secondary buttons above.
        "bg-white/10 text-white hover:bg-white/15 border border-white/15",
        "press transition-colors duration-quick ease-paper",
        "disabled:opacity-50 disabled:cursor-not-allowed",
      )}
    >
      <Icon size={20} aria-hidden />
    </button>
  );
}
